import type { CSSProperties, ImgHTMLAttributes } from "react";
import { cn } from "@skypointcloud/skypoint-ui";

interface ThemedLogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** Dark-ink artwork, for light backgrounds. */
  src: string;
  /** Light-ink artwork, for dark backgrounds. */
  darkSrc: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  /** How the dark copy displays once shown. Default "block". */
  display?: "block" | "inline" | "inline-block";
}

const SHOW_DARK = {
  "block": "dark:block",
  "inline": "dark:inline",
  "inline-block": "dark:inline-block",
} as const;

/**
 * A brand mark with artwork for each mode.
 *
 * The marks are drawn in near-black ink (#191919), which all but disappears on
 * the dark canvas. Both copies are rendered and CSS picks one: `dark:` keys off
 * the .sky-dark class index.html's pre-paint script sets, so the loading screen
 * shows the right logo before React has mounted. A React swap on isDarkMode
 * would show the wrong one first.
 *
 * The hidden copy is display:none, so assistive tech announces the logo once.
 * Extra props (data-testid etc.) go on the light copy only, so a test still
 * finds exactly one element. An inline `display` would beat the classes and
 * show both, so it is dropped; pass layout through className.
 */
export function ThemedLogo({
  src,
  darkSrc,
  alt,
  className,
  style,
  display = "block",
  ...rest
}: ThemedLogoProps) {
  const { display: _ignored, ...imgStyle } = style ?? {};
  return (
    <>
      <img
        src={src}
        alt={alt}
        className={cn(className, "dark:hidden")}
        style={imgStyle}
        {...rest}
      />
      <img
        src={darkSrc}
        alt={alt}
        className={cn(className, "hidden", SHOW_DARK[display])}
        style={imgStyle}
      />
    </>
  );
}
